import React, { useEffect, useState, memo, useRef } from 'react';
import { Box, Divider, Typography } from '@mui/material';
import { useApi } from '../../../../../shared/hooks/useApi';
import CustomTable from '../../../../../shared/components/table/custom-table';
import { LoaderComponent } from '../../../../../shared/components/loaders/page-loader';
import { fetchProductComponentDetails } from './component-details-apis';

// Updated row interface with required parameters
interface ProductRow {
  shipmentId: string;
  productId: string;
  tcId: string;
  inputTcId: string;
  inputTcComponentId: string;
}

interface RenderDetailPanelProps {
  row: { original: ProductRow };
}

// Column definitions for Standard Info table
const standardColumns = [
  {
    accessorKey: 'inputTcComponentStandardLabelGrade',
    header: 'Standard Label Grade',
    minSize: 60,
    maxSize: 150,
  },
  {
    accessorKey: 'inputTcComponentCertifiedWeight',
    header: 'Certified Weight (kg)',
    minSize: 60,
    maxSize: 150,
  },
  {
    accessorKey: 'inputTcComponentCertifiedWeightUsed',
    header: 'Certified Weight Used (kg)',
    minSize: 60,
    maxSize: 150,
  },
  { accessorKey: 'inputTcComponentStandardLabelGradeDescription', 
    header: 'Standard Label Grade Description', 
    minSize: 60, 
    maxSize: 150 
  },
];

// Column definitions for Raw Material Info table
const rawMaterialColumns = [
  {
    accessorKey: 'inputTcComponentRawMaterialCode',
    header: 'Raw Material Code',
    minSize: 60,
    maxSize: 150,
  },
  {
    accessorKey: 'inputTcComponentRawMaterialPercentage',
    header: 'Raw Material Percentage',
    minSize: 60,
    maxSize: 150,
  },
  { accessorKey: 'inputTcComponentRawMaterialCodeDescription', 
    header: 'Raw Material Description', 
    minSize: 60, 
    maxSize: 150 
  },
];

const RenderDetailPanel = memo(({ row }: RenderDetailPanelProps) => {
  const { shipmentId, productId, tcId, inputTcId, inputTcComponentId } = row.original;
  const { loading, executeGet } = useApi();
  const [productDetails, setProductDetails] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    if (!shipmentId || !productId || !tcId || !inputTcId || !inputTcComponentId || hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    fetchProductComponentDetails(
      executeGet,
      tcId,
      shipmentId,
      productId,
      inputTcId,
      inputTcComponentId
    )
      .then((data) => setProductDetails(data?.data))
      .catch((err) => setError(err.message));
  }, [shipmentId, productId, tcId, inputTcId, inputTcComponentId, executeGet]);

  if (loading) {
    return <LoaderComponent />;
  }
  if (error) {
    return <Typography color="error">{error}</Typography>;
  }
  if (!productDetails) {
    return <Typography>Loading product details...</Typography>;
  }

  // Extract nested data from response using the updated keys
  const componentInfo = productDetails.inputTcComponentInformation?.[0] || {};
  const standardInfo = componentInfo.inputTcComponentStandardInformation || [];
  const rawMaterialInfo = componentInfo.inputComponentRawMaterialInformation || [];

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        p: 5,
        gap: 4,
        width: '100%',
      }}
    >
      {/* Standard Info */}
      <Box sx={{ width: '100%' }}>
        <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
          Product Component Standard Info
        </Typography>
        <CustomTable
          columns={standardColumns}
          data={standardInfo}
          enableTopToolbar={false}
          enableBottomToolbar={false}
        />
      </Box>
      <Divider />
      {/* Raw Material Info */}
      <Box sx={{ width: '100%' }}>
        <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
          Raw Material Info
        </Typography>
        <CustomTable
          columns={rawMaterialColumns}
          data={rawMaterialInfo}
          enableTopToolbar={false}
          enableBottomToolbar={false}
        />
      </Box>
    </Box>
  );
});

export default RenderDetailPanel;
